/** Closing a period, reopening it, and what a closed period refuses — **ADR-0094**.
 *
 * Reads and writes only. Every sentence a refusal shows is the server's (design rule 7: written
 * once, so two screens cannot describe one fact differently); this module decides *which kind* of
 * refusal it was, because the remedies differ and a screen holding only a string cannot tell them
 * apart — the lesson `refusal.ts` opens with.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom, Refused } from "./refusal";
import type { components } from "./schema";

export type PeriodLock = components["schemas"]["PeriodLockOut"];
export type PeriodLockEvent = components["schemas"]["PeriodLockEventOut"];
export type ReopenResult = components["schemas"]["ReopenOut"];
export type WindowLock = components["schemas"]["WindowLockOut"];
export type ReopenPreview = components["schemas"]["ReopenPreviewOut"];

/** The longest reason the server accepts (`period_lock.MAX_REASON_CHARS`, ADR-0094 Q8). */
export const MAX_REASON_CHARS = 500;

/** What a person types to confirm they understand an issued filing stays as filed.
 *
 * The server's field is a boolean (`acknowledge_issued`); a typed phrase is the screen's way of
 * making that boolean a decision rather than a tick someone passes over. Compared trimmed and
 * case-insensitively — the point is to have read the sentence, not to punish a capital letter. */
export const ISSUED_PHRASE = "keep as filed";

export function acknowledgesIssued(typed: string): boolean {
  return typed.trim().toLowerCase() === ISSUED_PHRASE;
}

export const lockKey = (periodId: string) => ["period-lock", periodId] as const;

/** Whether a period is closed, and its history. Readable by anyone who can read the inventory;
 * the *words* of a reason come back only for an Admin, an Approver or an Auditor. */
export function usePeriodLock(periodId: string | undefined) {
  return useQuery({
    queryKey: lockKey(periodId ?? "none"),
    enabled: Boolean(periodId),
    // A fact that changes under a screen left open (someone else closes it), so never reused.
    staleTime: 0,
    queryFn: async (): Promise<PeriodLock> => {
      const { data, error, response } = await api.GET("/api/v1/periods/{period_id}/lock", {
        params: { path: { period_id: periodId as string } },
      });
      // A failed read throws. Returning null here would render as "not closed", which is the one
      // claim that lets someone write into a finished year.
      if (error || !data) throw refusalFrom(response, error, "This period's lock could not be read.");
      return data;
    },
  });
}

/** What reopening this period would do, **read before the act** (B14).
 *
 * The count of filing approvals a reopen withdraws comes from the same statement the reopen runs,
 * so the number here and the number the act then returns are two readings of one thing. It is a
 * read, not a reservation: someone deciding in between moves the act's own count, and the act's
 * count is the record. Never reused: an approval decided while the screen is open changes it.
 *
 * A failed read throws. Rendering it as "no approvals would be withdrawn" is a claim that lets
 * someone reopen believing nothing stands on the period. */
export function useReopenPreview(periodId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["reopen-preview", periodId ?? "none"] as const,
    enabled: Boolean(periodId) && enabled,
    staleTime: 0,
    queryFn: async (): Promise<ReopenPreview> => {
      const { data, error, response } = await api.GET("/api/v1/periods/{period_id}/reopen-preview", {
        params: { path: { period_id: periodId as string } },
      });
      if (error || !data) throw refusalFrom(response, error, "What a reopen would do could not be read.");
      return data;
    },
  });
}

/** Reopen a closed period. An Admin's reasoned act; the server checks the role again.
 *
 * `expectedClosureSeq` is the sequence the screen was looking at — **required**, because a stale
 * screen could otherwise undo a close it never saw. `acknowledgeIssued` is sent true only when the
 * person typed the phrase; the server refuses a reopen over an issued filing without it. */
export function useReopenPeriod(periodId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      reason: string;
      expectedClosureSeq: number;
      acknowledgeIssued: boolean;
    }): Promise<ReopenResult> => {
      if (!periodId) throw new Error("no period to reopen");
      const { data, error, response } = await api.POST("/api/v1/periods/{period_id}/reopen", {
        params: { path: { period_id: periodId } },
        body: {
          reason: input.reason,
          expected_closure_seq: input.expectedClosureSeq,
          acknowledge_issued: input.acknowledgeIssued,
        },
      });
      if (error || !data) throw refusalFrom(response, error, "That period could not be reopened.");
      return data;
    },
    onSuccess: async () => {
      // Everything: a reopen changes what every total says about its window (`window_lock`), the
      // filing verdict and the approvals. Naming keys is a list to keep in step with every screen.
      await queryClient.invalidateQueries();
    },
  });
}

/** The kinds of refusal a reopen can meet, as a screen branches on them. `other` is anything the
 * server did not give a code this module knows. */
export type ReopenRefusalKind =
  | "stale_closure"
  | "not_closed"
  | "already_reopened"
  | "issued_acknowledgement_required"
  | "invalid_post_close_reason"
  | "forbidden"
  | "other";

export function reopenRefusalKind(error: unknown): ReopenRefusalKind {
  if (!(error instanceof Refused)) return "other";
  if (error.status === 403) return "forbidden";
  switch (error.code) {
    case "stale_closure":
    case "not_closed":
    case "already_reopened":
    case "issued_acknowledgement_required":
    case "invalid_post_close_reason":
      return error.code;
    default:
      return "other";
  }
}
